import type { AdminGiftcardTransaction } from "../../lib/api/adminTransactionsData";

type RateRow = {
  range?: { min?: number; max?: number };
  type?: string;
  country?: string;
  rate?: number;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function giftcardTypeLabel(typeRaw: unknown): string {
  const s = str(typeRaw);
  if (!s) return "—";
  const spaced = s.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function rateCoversLeg(
  r: RateRow,
  txType: string,
  rLo: number,
  rHi: number,
  countryFilter?: string
): boolean {
  if (str(r.type) !== txType) return false;
  const rmin = num(r.range?.min);
  const rmax = num(r.range?.max);
  if (rmin > rLo || rmax < rHi) return false;
  if (countryFilter && r.country != null) {
    return str(r.country).toUpperCase() === countryFilter;
  }
  return true;
}

function findGiftcardRate(
  rates: RateRow[],
  txType: string,
  rLo: number,
  rHi: number,
  countryFilter?: string
): RateRow | undefined {
  if (countryFilter) {
    const withCountry = rates.find((r) =>
      rateCoversLeg(r, txType, rLo, rHi, countryFilter)
    );
    if (withCountry) return withCountry;
  }
  // Legacy GiftcardOrders: when `country` is set on the txn it still matches by type + range only.
  return rates.find((r) => rateCoversLeg(r, txType, rLo, rHi));
}

/**
 * NGN to pay and total face value in USD, using card.rates + tradeValue + type (+ country when valid).
 * API often stores the card name in `country` (e.g. "APPLE"); skip country filter in that case.
 */
export function computeGiftcardNgnAndUsd(
  tx: AdminGiftcardTransaction
): { ngn: number; usd: number; rateMatched: boolean } {
  const card = tx.card;
  const tradeValue = tx.tradeValue;
  if (!card || typeof card !== "object" || !Array.isArray(tradeValue)) {
    return { ngn: 0, usd: 0, rateMatched: false };
  }

  const rates = (card as { rates?: RateRow[] }).rates;
  if (!Array.isArray(rates)) return { ngn: 0, usd: 0, rateMatched: false };

  const txType = str(tx.type);
  const cardName = giftcardCardName(tx).toUpperCase();
  const txCountry = str(tx.country).toUpperCase();
  const countryFilter =
    txCountry && txCountry !== cardName ? txCountry : undefined;

  let ngn = 0;
  let usd = 0;
  let rateMatched = true;

  for (const leg of tradeValue) {
    if (!leg || typeof leg !== "object") continue;
    const range = (leg as { range?: { min?: number; max?: number } }).range;
    const amount = num((leg as { amount?: unknown }).amount);
    usd += amount;
    if (
      !range ||
      !Number.isFinite(Number(range.min)) ||
      !Number.isFinite(Number(range.max))
    ) {
      rateMatched = false;
      continue;
    }

    const rLo = num(range.min);
    const rHi = num(range.max);
    const rateInfo = findGiftcardRate(rates, txType, rLo, rHi, countryFilter);

    if (rateInfo && typeof rateInfo.rate === "number") {
      ngn += amount * rateInfo.rate;
    } else {
      rateMatched = false;
    }
  }

  return { ngn, usd, rateMatched };
}

export function giftcardCardName(tx: AdminGiftcardTransaction): string {
  const c = tx.card;
  if (c && typeof c === "object" && "name" in c) {
    return str((c as { name?: unknown }).name) || "—";
  }
  return "—";
}

export function giftcardImageUrls(tx: AdminGiftcardTransaction): string[] {
  const imgs = tx.images;
  if (!Array.isArray(imgs)) return [];
  return imgs.filter((u): u is string => typeof u === "string" && u.length > 0);
}

export function giftcardEcode(tx: AdminGiftcardTransaction): string {
  if ("code" in tx && typeof tx.code === "string") return tx.code;
  return "";
}

export function getGiftcardDisplayDate(tx: AdminGiftcardTransaction): string {
  const s = str(tx.createdAt);
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}
